import React, { useEffect, useState } from 'react';
import type { Node, Edge } from 'reactflow';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { PrimitiveNodeData } from './PrimitiveNode';
import type { CatalogAction } from './workflowGraphTypes';
import DynamicMultiselectField from './DynamicMultiselectField';
import MemberSelectField from './MemberSelectField';
import {
  EDGE_OPERATOR_OPTIONS,
  NUMERIC_BRANCH_CONDITIONS_ENABLED,
  isValidBranchCondition,
  type EdgeComparisonOperator,
  type BranchCondition,
} from './edgeConditionUtils';

// Same small info-icon-with-tooltip pattern already used in WorkflowToolbar.tsx and
// WorkflowCanvasTab.tsx — kept as a local copy here too rather than a shared export, matching
// how those two files each define their own copy.
const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>{text}</Box>}
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} tabIndex={-1}>
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

interface WorkflowInspectorPanelProps {
  selectedNode: Node<PrimitiveNodeData> | null;
  selectedEdge: Edge | null;
  onUpdateNode: (id: string, data: Partial<PrimitiveNodeData>) => void;
  onUpdateEdgeCondition: (id: string, update: { condition: string | null; conditionOp: BranchCondition | null }) => void;
  onDeleteSelected: () => void;
  /** NEW — the full canvas graph, used only to find upstream nodes for the action field-picker. */
  nodes?: Node<PrimitiveNodeData>[];
  edges?: Edge[];
  /** NEW — action_id → catalog action lookup (ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §3). */
  catalogActionsById?: Record<string, CatalogAction>;
  /** dynamic_multiselect_FE_datasource_picker_spec.md — the enclosing channel, threaded down to
   * DynamicMultiselectField so its live fetch can be scoped. See that component's doc comment for
   * why this is ambient context here rather than a sibling form field. */
  channelId?: string;
}

interface UpstreamOutput {
  nodeId: string;
  nodeName: string;
  outputKey: string;
  outputLabel: string;
}

/**
 * Field-picker source data: for every node reachable backward (via edges) from `nodeId`,
 * list the declared `definition.outputs` of any upstream node that was itself action-authored.
 * Raw primitive upstream nodes have no declared outputs list and are not offered here
 * (ref: 06_...md §7 — "list definition.outputs from every node reachable backward").
 */
function getUpstreamActionOutputs(
  nodeId: string,
  nodes: Node<PrimitiveNodeData>[],
  edges: Edge[],
  catalogActionsById: Record<string, CatalogAction>
): UpstreamOutput[] {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>([nodeId]);
  const queue = [nodeId];
  const results: UpstreamOutput[] = [];

  while (queue.length) {
    const current = queue.shift() as string;
    for (const edge of edges) {
      if (edge.target !== current || visited.has(edge.source)) continue;
      visited.add(edge.source);
      queue.push(edge.source);
      const upstreamNode = nodesById.get(edge.source);
      const actionId = upstreamNode?.data?.actionId;
      const action = actionId ? catalogActionsById[actionId] : undefined;
      if (upstreamNode && action) {
        for (const output of action.definition.outputs ?? []) {
          results.push({
            nodeId: upstreamNode.id,
            nodeName: upstreamNode.data.name,
            outputKey: output.key,
            outputLabel: output.label,
          });
        }
      }
    }
  }
  return results;
}

/** JSON-object config field (body/params/variables) — free-text, parsed on blur. */
function JsonField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value ?? {}, null, 2));
  }, [value]);

  return (
    <TextField
      label={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        try {
          const parsed = text.trim() === '' ? {} : JSON.parse(text);
          setError(null);
          onChange(parsed);
        } catch {
          setError('Invalid JSON');
        }
      }}
      error={Boolean(error)}
      helperText={error ?? 'JSON object — supports {placeholder} interpolation from context'}
      multiline
      minRows={3}
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
  );
}

/**
 * Key/value row editor for a catalog action input typed `object` (e.g. `core.create_record`'s
 * `payload`, `notify.send_template`/`notify.send_custom`'s `variables`) — the user never sees or
 * types `{`, `}`, or quotes; rows are serialized into a plain object on every change
 * (ref: Frontend action items 2026-07-24 §3).
 */
function ObjectFieldEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const [rows, setRows] = useState<Array<{ key: string; value: string }>>(() =>
    Object.entries(value ?? {}).map(([k, v]) => ({ key: k, value: String(v ?? '') }))
  );

  const commit = (nextRows: Array<{ key: string; value: string }>) => {
    setRows(nextRows);
    const obj: Record<string, unknown> = {};
    for (const row of nextRows) {
      if (row.key.trim() === '') continue;
      obj[row.key] = row.value;
    }
    onChange(obj);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      {rows.map((row, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            placeholder="Field name"
            value={row.key}
            onChange={(e) => commit(rows.map((r, idx) => (idx === i ? { ...r, key: e.target.value } : r)))}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            placeholder="Value"
            value={row.value}
            onChange={(e) => commit(rows.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)))}
            size="small"
            sx={{ flex: 1 }}
          />
          <IconButton size="small" onClick={() => commit(rows.filter((_, idx) => idx !== i))} aria-label="Remove field">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={() => commit([...rows, { key: '', value: '' }])}
        sx={{ textTransform: 'none' }}
      >
        Add field
      </Button>
    </Box>
  );
}

/**
 * FIX2_FE_numeric_branch_comparison_spec.md §2/§3, corrected per PR #669 review — BRANCH edge
 * condition editor. Defaults to the legacy plain-string box (§3.2); "Add a numeric comparison
 * instead" swaps in a Field input + operator dropdown + numeric value field (§2.1/§2.2) — `field`
 * names the context value being compared (e.g. "days_requested"), required by the orchestrator's
 * BranchCondition schema, and was missing entirely before this fix. An edge that already carries
 * a valid condition_op opens directly into that view, pre-filled (§3.2 point 3). Keyed by
 * `edgeId` from its one call site below, so selecting a different edge always remounts this with
 * fresh local state — the `useEffect` re-sync here is a second-layer guard for that, matching
 * ObjectFieldEditor's own key-vs-effect reasoning above.
 *
 * (C2) The "Add a numeric comparison instead" link only appears for a fresh edge when
 * NUMERIC_BRANCH_CONDITIONS_ENABLED is true (currently false — the orchestrator rejects
 * condition_op outright until its interpreter ships). An edge that already has one loaded stays
 * fully editable in the structured view regardless, so existing data is never hidden or stripped.
 *
 * (M1) An invalid value no longer leaves the previously committed value sitting on the edge: every
 * keystroke commits immediately, clearing condition_op to null while the value is invalid rather
 * than silently keeping the last valid one.
 * (M2) Switching modes clears the other shape's field immediately (not just when a new value
 * becomes valid), so an edge is never left carrying a stale leftover from the abandoned mode.
 */
function EdgeConditionEditor({
  edgeId,
  rawCondition,
  rawConditionOp,
  onUpdate,
}: {
  edgeId: string;
  rawCondition: string | null | undefined;
  rawConditionOp: BranchCondition | null | undefined;
  onUpdate: (id: string, update: { condition: string | null; conditionOp: BranchCondition | null }) => void;
}) {
  const initialStructured = isValidBranchCondition(rawConditionOp) ? rawConditionOp : null;
  const [mode, setMode] = useState<'string' | 'structured'>(initialStructured ? 'structured' : 'string');
  const [stringValue, setStringValue] = useState(() => rawCondition ?? '');
  const [fieldText, setFieldText] = useState(() => initialStructured?.field ?? '');
  const [op, setOp] = useState<EdgeComparisonOperator>(initialStructured?.op ?? '==');
  const [valueText, setValueText] = useState(() => (initialStructured ? String(initialStructured.value) : ''));
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [valueError, setValueError] = useState<string | null>(null);

  useEffect(() => {
    const structured = isValidBranchCondition(rawConditionOp) ? rawConditionOp : null;
    setMode(structured ? 'structured' : 'string');
    setStringValue(rawCondition ?? '');
    setFieldText(structured?.field ?? '');
    setOp(structured?.op ?? '==');
    setValueText(structured ? String(structured.value) : '');
    setFieldError(null);
    setValueError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeId]);

  // (N2) PR #669 review — the orchestrator's BranchCondition.value is a float with no documented
  // lower bound; this no longer rejects decimals or negatives (the original integer/>=0 rule in
  // this spec doc didn't match the live backend schema). Still required, still a real number.
  // (M1) Every call commits immediately — an invalid field/value commits condition_op: null
  // instead of leaving whatever was last valid sitting on the edge.
  const validateAndCommit = (nextField: string, nextOp: EdgeComparisonOperator, nextValueText: string) => {
    setFieldText(nextField);
    setOp(nextOp);
    setValueText(nextValueText);

    const trimmedField = nextField.trim();
    const trimmedValue = nextValueText.trim();
    const nextFieldError = trimmedField === '' ? 'Field is required' : null;
    const parsedValue = trimmedValue === '' ? NaN : Number(trimmedValue);
    const nextValueError =
      trimmedValue === '' ? 'Value is required' : !Number.isFinite(parsedValue) ? 'Must be a number' : null;
    setFieldError(nextFieldError);
    setValueError(nextValueError);

    if (nextFieldError || nextValueError) {
      onUpdate(edgeId, { condition: null, conditionOp: null });
      return;
    }
    onUpdate(edgeId, { condition: null, conditionOp: { op: nextOp, field: trimmedField, value: parsedValue } });
  };

  if (mode === 'string') {
    return (
      <>
        <TextField
          label="Condition (leave blank for unconditional)"
          value={stringValue}
          onChange={(e) => {
            setStringValue(e.target.value);
            onUpdate(edgeId, { condition: e.target.value || null, conditionOp: null });
          }}
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <FieldHelp text={`Matches this edge's exact text against the previous step's result (e.g. an approval status like "approved"). Leave blank for an edge that's always taken.`} />
              </InputAdornment>
            ),
          }}
        />
        {(NUMERIC_BRANCH_CONDITIONS_ENABLED || initialStructured) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 2 }}>
            <Button
              size="small"
              onClick={() => {
                setMode('structured');
                // (M2) clear the string condition immediately — never leave it committed
                // underneath while the structured view is showing, even before a valid value exists.
                setFieldText('');
                setOp('==');
                setValueText('');
                setFieldError(null);
                setValueError(null);
                onUpdate(edgeId, { condition: null, conditionOp: null });
              }}
              sx={{ textTransform: 'none' }}
            >
              Add a numeric comparison instead
            </Button>
            <FieldHelp
              text={
                "Compares a value from the workflow's context (e.g. a count or duration produced by " +
                "an earlier step) against a fixed number, using an operator like \"less than\", instead " +
                "of matching an exact text value.\nNot yet supported by the workflow runtime — saving " +
                "an edge like this will fail validation until that support ships."
              }
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {!NUMERIC_BRANCH_CONDITIONS_ENABLED && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Numeric comparisons aren't runnable yet — this edge will be rejected on save/run-test
          until the workflow runtime supports it.
        </Alert>
      )}
      <TextField
        label="Field (e.g. days_requested)"
        value={fieldText}
        onChange={(e) => validateAndCommit(e.target.value, op, valueText)}
        error={Boolean(fieldError)}
        helperText={fieldError ?? 'The context value to compare'}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <FieldHelp text={"The context value to compare — e.g. a number produced by an earlier step's output, or a value from the triggering message."} />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, mb: 1.5 }}>
        <TextField
          select
          label="Operator"
          value={op}
          onChange={(e) => validateAndCommit(fieldText, e.target.value as EdgeComparisonOperator, valueText)}
          fullWidth
          size="small"
        >
          {EDGE_OPERATOR_OPTIONS.map((o) => (
            <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ pt: 1.25 }}>
          <FieldHelp text={`How to compare the field against the value — e.g. "Less than" takes this edge only when the field's value is smaller.`} />
        </Box>
      </Box>
      <TextField
        label="Value"
        type="number"
        value={valueText}
        onChange={(e) => validateAndCommit(fieldText, op, e.target.value)}
        error={Boolean(valueError)}
        helperText={valueError ?? 'Any number'}
        fullWidth
        size="small"
        sx={{ mb: 1 }}
        inputProps={{ step: 'any' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <FieldHelp text="The fixed number to compare the field against." />
            </InputAdornment>
          ),
        }}
      />
      <Button
        size="small"
        onClick={() => {
          setMode('string');
          setStringValue('');
          onUpdate(edgeId, { condition: null, conditionOp: null });
        }}
        sx={{ textTransform: 'none' }}
      >
        Use a text condition instead
      </Button>
    </Box>
  );
}

/** Node/edge inspector — shows the selected node or edge's editable config fields (ref: spec §2). */
const WorkflowInspectorPanel: React.FC<WorkflowInspectorPanelProps> = ({
  selectedNode,
  selectedEdge,
  onUpdateNode,
  onUpdateEdgeCondition,
  onDeleteSelected,
  nodes = [],
  edges = [],
  catalogActionsById = {},
  channelId,
}) => {
  if (!selectedNode && !selectedEdge) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Inspector
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a node or edge on the canvas to edit its configuration.
        </Typography>
      </Box>
    );
  }

  if (selectedEdge) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Edge: {selectedEdge.source} → {selectedEdge.target}
        </Typography>
        <EdgeConditionEditor
          key={selectedEdge.id}
          edgeId={selectedEdge.id}
          rawCondition={selectedEdge.data?.condition as string | null | undefined}
          rawConditionOp={selectedEdge.data?.condition_op as BranchCondition | null | undefined}
          onUpdate={onUpdateEdgeCondition}
        />
        <Button color="error" size="small" variant="outlined" onClick={onDeleteSelected} sx={{ textTransform: 'none' }}>
          Delete edge
        </Button>
      </Box>
    );
  }

  const node = selectedNode!;

  // Action-authored node: render the friendly `inputs` form from the catalog action's
  // definition — raw type/config editing is hidden entirely for this node (ref: 06_...md §3, §7).
  if (node.data.actionId) {
    const action = catalogActionsById[node.data.actionId];
    const inputs = node.data.inputs ?? {};
    const setInputs = (patch: Record<string, unknown>) =>
      onUpdateNode(node.id, { inputs: { ...inputs, ...patch } });
    const upstreamOutputs = getUpstreamActionOutputs(node.id, nodes, edges, catalogActionsById);

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {action?.label ?? node.data.actionId}
        </Typography>
        {action?.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {action.description}
          </Typography>
        )}
        {!action && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            "{node.data.actionId}" is not a registered catalog action — this step can't be saved
            or run until it's fixed or removed.
          </Alert>
        )}

        <TextField
          label="Name"
          value={node.data.name}
          onChange={(e) => onUpdateNode(node.id, { name: e.target.value })}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <FieldHelp text={'A label for this step, shown on the canvas node.\nFor your reference only — it doesn\'t affect what the step does when the workflow runs.'} />
              </InputAdornment>
            ),
          }}
        />

        {(action?.definition.inputs ?? []).length === 0 && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            This step runs automatically using information already in this workflow — nothing to fill in.
          </Alert>
        )}

        {(action?.definition.inputs ?? []).map((field) =>
          field.type === 'dynamic_multiselect' ? (
            // dynamic_multiselect_FE_datasource_picker_spec.md §4 — the one field type whose
            // options aren't baked into the catalog; see DynamicMultiselectField's own doc
            // comment for the live-fetch/debounce/cancellation/pagination behavior.
            <DynamicMultiselectField
              key={`${node.id}-${field.key}`}
              label={field.label}
              required={field.required}
              endpoint={field.source?.endpoint}
              channelId={channelId}
              value={(inputs[field.key] as string[]) ?? []}
              onChange={(ids) => setInputs({ [field.key]: ids })}
            />
          ) : field.type === 'member_select' ? (
            // FE-07_approver_per_run_or_fixed.md — "Fixed approver" nice-to-have: a single channel
            // member (or a manually typed email), for a field that should hold one fixed assignee
            // rather than a per-run {token}. Stores the exact same plain email string the field
            // already held as plain text — only the editing UX changes. See MemberSelectField's
            // own doc comment for why it's a new component rather than reusing RecipientPicker.tsx
            // directly (that one is hard-coded to a multi-select array for a different canvas).
            <Box key={field.key} sx={{ mb: 2 }}>
              <MemberSelectField
                label={field.label}
                required={field.required}
                channelId={channelId}
                value={(inputs[field.key] as string) ?? ''}
                onChange={(email) => setInputs({ [field.key]: email })}
              />
              {/* Same FE-7 hint as the plain-text approver_email/assignee_email case below — a
                  missing/unknown email fails the run the same way regardless of how it was set. */}
              {(field.key === 'approver_email' || field.key === 'assignee_email') && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  A missing or unknown email here fails the run. Consider adding a BRANCH to check
                  it isn't empty first.
                </Typography>
              )}
            </Box>
          ) : field.type === 'object' ? (
            <ObjectFieldEditor
              // PR review fix: key by node id + field key, not field key alone. Two different
              // catalog actions can share an object-field key (e.g. "payload"/"variables"), so
              // keying by field.key alone left this component mounted across a node switch —
              // its local `rows` state kept showing/saving the previously selected node's data.
              key={`${node.id}-${field.key}`}
              label={field.required ? `${field.label} *` : field.label}
              value={(inputs[field.key] as Record<string, unknown>) ?? {}}
              onChange={(next) => setInputs({ [field.key]: next })}
            />
          ) : field.type === 'enum' ? (
            // Generic enum rendering — driven entirely by definition.inputs[].type/.options
            // from the catalog, not hardcoded per field key. Applies to any current or future
            // enum field (e.g. Custom API Request's Method and Request path, Wait for
            // approval's Approval channel) automatically, with no further frontend change
            // (ref: 17_Frontend_Custom_API_Request_Path_Dropdown_2026-08-03.md).
            // Help icon sits beside the Select, not inside it as an endAdornment — same reason
            // as the "Insert from an earlier step" picker below: an endAdornment there would
            // collide with MUI's own dropdown arrow in that same corner.
            <Box key={field.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, mb: 2 }}>
              <TextField
                select
                label={field.required ? `${field.label} *` : field.label}
                value={(inputs[field.key] as string) ?? ''}
                onChange={(e) => setInputs({ [field.key]: e.target.value })}
                fullWidth
                size="small"
              >
                {(field.options ?? []).map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ pt: 1.25 }}>
                <FieldHelp text={`Select a value for "${field.label}".`} />
              </Box>
            </Box>
          ) : field.type === 'boolean' ? (
            // PRISM7_TEMPORAL_FRONTEND_CHANGES.md FE-10 — new field type for a plain on/off
            // input (e.g. data_ai.ask's upcoming `combine` option). Boolean(...) rather than a
            // bare cast: a freshly-added field has no value in `inputs` yet (undefined), and
            // MUI's Switch requires a real boolean for `checked` or it logs a controlled/
            // uncontrolled-component warning.
            // PR #670 review (M1) — an untouched field falls back to field.default (what the
            // orchestrator's own template default resolves to at compile time, workflow_compile.py
            // _resolve_template), not straight to false. Without this, a field whose catalog
            // default is true rendered OFF here while the run actually behaved as ON.
            <Box key={field.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(inputs[field.key] ?? field.default ?? false)}
                    onChange={(e) => setInputs({ [field.key]: e.target.checked })}
                    size="small"
                  />
                }
                label={field.required ? `${field.label} *` : field.label}
              />
              <FieldHelp text={`Turn "${field.label}" on or off.`} />
            </Box>
          ) : (
            <Box key={field.key} sx={{ mb: 2 }}>
              <TextField
                label={field.required ? `${field.label} *` : field.label}
                value={(inputs[field.key] as string) ?? ''}
                onChange={(e) => setInputs({ [field.key]: e.target.value })}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FieldHelp
                        text={
                          upstreamOutputs.length > 0
                            ? `Enter a value directly, or leave this blank and use "Insert from an earlier step" below to pull it from a previous step's output instead.`
                            : `Enter a value for "${field.label}".`
                        }
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {/* PRISM7_TEMPORAL_FRONTEND_CHANGES.md FE-7 — approver_email/assignee_email are
                  plain catalog-authored text fields with no special renderer of their own; this
                  is the one extra, field-key-specific warning in an otherwise fully generic loop.
                  A missing/unknown email here fails the whole run (base_shay returns a 404) with
                  no earlier, friendlier signal, so flag it directly under the field rather than
                  only in a hover tooltip.
                  PR #670 review (N1) — matched on the exact two email-field keys, not a loose
                  substring test: the old /approver|assignee/i pattern also fired on something
                  like approver_notes or assignee_id, which aren't emails and don't fail the run
                  the way an empty/unknown email does. */}
              {(field.key === 'approver_email' || field.key === 'assignee_email') && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  A missing or unknown email here fails the run. Consider adding a BRANCH to check
                  it isn't empty first.
                </Typography>
              )}
              {upstreamOutputs.length > 0 && (
                // Info icon sits beside the Select, not inside it as an endAdornment — an
                // endAdornment there would sit at the same corner as MUI's own dropdown arrow
                // and overlap it. Same pattern as the "Open…" workflow picker in WorkflowToolbar.
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, mt: 2 }}>
                  <TextField
                    select
                    label="Insert from an earlier step"
                    value=""
                    onChange={(e) => setInputs({ [field.key]: e.target.value })}
                    fullWidth
                    size="small"
                    SelectProps={{ displayEmpty: true }}
                    // Always float the label — this field's value is always "" (it's a one-shot
                    // picker, never retains a selection), but its MenuItem placeholder text still
                    // renders via displayEmpty. Without forcing shrink, MUI keeps the label
                    // vertically centered (since it never sees focus/a value to trigger the
                    // float), landing it directly on top of that placeholder text inside the box.
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">
                      {/* Plain body2/text.secondary — matches the disabled-placeholder style
                          used elsewhere (e.g. "No saved versions yet" in WorkflowToolbar),
                          instead of the browser-default italic <em> font this used to render
                          with. */}
                      <Typography variant="body2" color="text.secondary">
                        Pick one option below
                      </Typography>
                    </MenuItem>
                    {upstreamOutputs.map((o) => (
                      <MenuItem key={`${o.nodeId}.${o.outputKey}`} value={o.outputKey}>
                        From "{o.nodeName}" → {o.outputLabel}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ pt: 1.25 }}>
                    <FieldHelp text={"Optional. Picking a step here overwrites whatever you typed above with that step's output when this workflow runs."} />
                  </Box>
                </Box>
              )}
            </Box>
          )
        )}

        {(action?.definition.outputs ?? []).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.5 }}>
              Produces
            </Typography>
            {(action?.definition.outputs ?? []).map((output) => (
              <Typography key={output.key} variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                {output.label} — available to use in later steps.
              </Typography>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Button color="error" size="small" variant="outlined" onClick={onDeleteSelected} sx={{ textTransform: 'none' }}>
          Delete node
        </Button>
      </Box>
    );
  }

  const config = node.data.config ?? {};
  const setConfig = (patch: Record<string, unknown>) =>
    onUpdateNode(node.id, { config: { ...config, ...patch } });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {node.data.displayLabel ?? node.data.name}
      </Typography>
      <TextField
        label="Name"
        value={node.data.name}
        onChange={(e) => onUpdateNode(node.id, { name: e.target.value })}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {node.data.primType === 'CALL_API' && (
        <>
          <TextField
            select
            label="Method"
            value={(config.method as string) ?? 'GET'}
            onChange={(e) => setConfig({ method: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Path"
            value={(config.path as string) ?? ''}
            onChange={(e) => setConfig({ path: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <JsonField label="Body" value={config.body as Record<string, unknown>} onChange={(v) => setConfig({ body: v })} />
          <JsonField label="Params (GET query)" value={config.params as Record<string, unknown>} onChange={(v) => setConfig({ params: v })} />
          <TextField
            label="Result key"
            value={(config.result_key as string) ?? ''}
            onChange={(e) => setConfig({ result_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      {node.data.primType === 'CLASSIFY' && (
        <>
          <TextField
            label="Tags"
            value={(config.input_key as string) ?? ''}
            onChange={(e) => setConfig({ input_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Sub Tags"
            value={(config.result_key as string) ?? ''}
            onChange={(e) => setConfig({ result_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      {node.data.primType === 'NOTIFY' && (
        <>
          <TextField
            label="Template ID (leave blank to use inline subject/body)"
            value={(config.template_id as string) ?? ''}
            onChange={(e) => setConfig({ template_id: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Subject"
            value={(config.subject as string) ?? ''}
            onChange={(e) => setConfig({ subject: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Body"
            value={(config.body as string) ?? ''}
            onChange={(e) => setConfig({ body: e.target.value })}
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Recipient email key"
            value={(config.recipient_email_key as string) ?? ''}
            onChange={(e) => setConfig({ recipient_email_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <JsonField label="Variables" value={config.variables as Record<string, unknown>} onChange={(v) => setConfig({ variables: v })} />
          <TextField
            label="CC emails (comma-separated)"
            value={((config.cc_emails as string[]) ?? []).join(', ')}
            onChange={(e) => setConfig({ cc_emails: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="BCC emails (comma-separated)"
            value={((config.bcc_emails as string[]) ?? []).join(', ')}
            onChange={(e) => setConfig({ bcc_emails: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      {node.data.primType === 'WAIT_SIGNAL' && (
        <>
          <TextField
            label="Signal name"
            value={(config.signal_name as string) ?? ''}
            onChange={(e) => setConfig({ signal_name: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Timeout (hours)"
            type="number"
            value={(config.timeout_hours as number) ?? 24}
            onChange={(e) => setConfig({ timeout_hours: Number(e.target.value) })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Timeout condition (BRANCH value on timeout)"
            value={(config.timeout_condition as string) ?? ''}
            onChange={(e) => setConfig({ timeout_condition: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Result key"
            value={(config.result_key as string) ?? ''}
            onChange={(e) => setConfig({ result_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      {node.data.primType === 'BRANCH' && (
        <>
          <TextField
            label="Condition key (dotted path, e.g. approver_result.action)"
            value={(config.condition_key as string) ?? ''}
            onChange={(e) => setConfig({ condition_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Default condition (optional fallback)"
            value={(config.default_condition as string) ?? ''}
            onChange={(e) => setConfig({ default_condition: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      {node.data.primType === 'AGENT' && (
        <>
          <TextField
            label="Prompt (template, interpolated from context)"
            value={(config.prompt as string) ?? ''}
            onChange={(e) => setConfig({ prompt: e.target.value })}
            fullWidth
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Result key"
            value={(config.result_key as string) ?? ''}
            onChange={(e) => setConfig({ result_key: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        </>
      )}

      <Divider sx={{ my: 1 }} />
      <Button color="error" size="small" variant="outlined" onClick={onDeleteSelected} sx={{ textTransform: 'none' }}>
        Delete node
      </Button>
    </Box>
  );
};

export default WorkflowInspectorPanel;
